import { useState, useRef, useEffect } from "react";
import { uploadFileToServer } from "../../api/apiService";
// Components
import Input from "../Input";
import Checkbox from "../Checkbox";
import FileUploader from "components/FileUploader";
import { useSnackBar } from "../../components/snackBar/SnackBarContext"; // adjust the path as needed
// Assets
import Info from "../../assets/icons/ic_info.svg";

const TravelDetails = ({
  formData,
  formErrors,
  handleChange,
  index,
  inputRefs,
}) => {
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackBar();
  const successSoundRef = useRef(null);

  useEffect(() => {
    const audio = new Audio("/confirmation-tone.mp3");
    successSoundRef.current = audio;
  }, []);

  const handleFileChange = async (file) => {
    if (file) {
      // Upload file
      const uploadData = new FormData();
      uploadData.append("file", file);
      try {
        setLoading(true);
        // Show persistent snackbar while loading
        showSnackbar("info", "Uploading and scanning passport...");
        const response = await uploadFileToServer(uploadData);
        if (response) setLoading(false);
        showSnackbar("success", "Passport details auto-filled successfully.");

        const data = response[0];
        handleChange({
          target: { name: "passportExp", value: data.DateOfExpiry },
        });
        handleChange({
          target: { name: "passportNo", value: data.PassportNumber || "" },
        });
        handleChange({
          target: { name: "citizenship", value: data.Nationality || "" },
        });
        handleChange({
          target: { name: "dob", value: data.DateOfBirth || "" },
        });
        handleChange({
          target: { name: "passportExp", value: data.DateOfExpiry || "" },
        });

        // 👇 Auto-uncheck renewPassport when file uploaded
        if (formData.noPassport) {
          handleChange({
            target: { name: "noPassport", value: false },
          });
        }

        if (formData.passportConfirmation)
          handleChange({
            target: {
              name: "passportConfirmation",
              value: false,
            },
          });
      } catch (err) {
        setLoading(false);
        console.error("Upload Failed:", err);
        showSnackbar("error", "Failed to scan passport. Please try again.");
      }
    }
  };

  return (
    <div className="relative block sm:grid md:grid-cols-2 gap-4">
      {/* 📸 Pictures Wall for Passport */}
      {loading && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-6 w-6 border border-blue-500 border-t-transparent"></div>
        </div>
      )}

      {/* No Passport */}
      <div className="md:col-span-2">
        <div className="md:grid-cols-1">
          <Checkbox
            className="text-xs"
            name={`noPassport${index}`}
            checked={formData.noPassport === "Yes"} // 👈 string → boolean
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleChange({
                target: {
                  name: "noPassport",
                  value: isChecked ? "Yes" : "No", // 👈 send string
                },
              });
              if (isChecked)
                handleChange({
                  target: {
                    name: "renewPassport",
                    value: "No", // 👈 send string
                  },
                });

              if (isChecked && successSoundRef.current) {
                successSoundRef.current.currentTime = 0;
                successSoundRef.current.play();

                setTimeout(() => {
                  successSoundRef.current.pause();
                  successSoundRef.current.currentTime = 0;
                }, 2000);
              }
            }}
            label="I do not have a passport yet, but I'm in the process of applying for a passport."
          />
        </div>
      </div>

      {/* Renew Passport */}
      <div className="md:col-span-2 py-2 md:py-0">
        <div className="md:grid-cols-1">
          <Checkbox
            className="text-xs"
            name={`renewPassport${index}`}
            checked={formData.renewPassport === "Yes"}
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleChange({
                target: {
                  name: "renewPassport",
                  value: isChecked ? "Yes" : "No", // 👈 send string
                },
              });

              if (isChecked)
                handleChange({
                  target: {
                    name: "noPassport",
                    value: "No",
                  },
                });

              if (isChecked && successSoundRef.current) {
                successSoundRef.current.currentTime = 0;
                successSoundRef.current.play();

                setTimeout(() => {
                  successSoundRef.current.pause();
                  successSoundRef.current.currentTime = 0;
                }, 2000);
              }
            }}
            label={
              <span className="whitespace-normal">
                My passport has expired or it's expiring within 6 months from
                the last day of the tour. I plan to renew it and I will revise
                the passport information on this form once I receive the
                renewal.
              </span>
            }
          />
        </div>
      </div>

      <div className="col-span-2 md:pb-0 pb-2">
        <h3 className="text-base font-medium">Current Passport Information</h3>
        <p className="text-sm text-yellow-800">
          (Upload or take a photo of your passport to auto-fill your details.
          Please review and confirm them. If any details aren’t filled in
          automatically, enter them manually.)
        </p>
      </div>

      <div className="col-span-2 sm:w-[350px] w-full mb-2 justify-center align-middle">
        <FileUploader
          handleFileChange={handleFileChange}
          handleChange={handleChange}
          formData={formData}
          formErrors={formErrors}
        />
      </div>
      <Input
        label="Citizenship"
        name="citizenship"
        value={formData.citizenship}
        onChange={(e) => {
          handleChange(e);
          if (formData.passportConfirmation)
            handleChange({
              target: {
                name: "passportConfirmation",
                value: false,
              },
            });
        }}
        error={formErrors.citizenship}
        required
        ref={inputRefs.Citizenship}
      />

      <Input
        label="Passport Number"
        name="passportNo"
        value={formData.passportNo}
        onChange={(e) => {
          handleChange(e);
          if (formData.passportConfirmation)
            handleChange({
              target: {
                name: "passportConfirmation",
                value: false,
              },
            });
        }}
        required={formData.noPassport === "Yes" ? false : true} // 👈 required only if renewPassport is false
        error={formErrors.passportNo}
        ref={inputRefs.passportNo}
      />
      <Input
        label="Passport Expiry Date"
        name="passportExp"
        type="date"
        value={formData.passportExp}
        onChange={(e) => {
          handleChange(e);
          if (formData.passportConfirmation)
            handleChange({
              target: {
                name: "passportConfirmation",
                value: false,
              },
            });
        }}
        required={formData.noPassport === "Yes" ? false : true} // 👈 required only if renewPassport is false
        error={formErrors.passportExp}
        ref={inputRefs.passportExp}
      />
      <Input
        label="Date Of Birth"
        name="dob"
        type="date"
        value={formData.dob}
        onChange={(e) => {
          handleChange(e);
          if (formData.passportConfirmation)
            handleChange({
              target: {
                name: "passportConfirmation",
                value: false,
              },
            });
        }}
        error={formErrors.dob}
        required
        ref={inputRefs.dob}
      />
      {/* No Passport */}
      {/* <div className="md:col-span-2">
        <div className="md:grid-cols-1">
          <Checkbox
            className="text-xs"
            name={`noPassport${index}`}
            checked={formData.noPassport === "Yes"} // 👈 string → boolean
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleChange({
                target: {
                  name: "noPassport",
                  value: isChecked ? "Yes" : "No", // 👈 send string
                },
              });
              if (isChecked)
                handleChange({
                  target: {
                    name: "renewPassport",
                    value: "No", // 👈 send string
                  },
                });

              if (isChecked && successSoundRef.current) {
                successSoundRef.current.currentTime = 0;
                successSoundRef.current.play();

                setTimeout(() => {
                  successSoundRef.current.pause();
                  successSoundRef.current.currentTime = 0;
                }, 2000);
              }
            }}
            label="I do not have a passport yet, but I'm in the process of applying for a passport."
          />
        </div>
      </div> */}

      {/* Renew Passport */}
      {/* <div className="md:col-span-2 py-2 md:py-0">
        <div className="md:grid-cols-1">
          <Checkbox
            className="text-xs"
            name={`renewPassport${index}`}
            checked={formData.renewPassport === "Yes"}
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleChange({
                target: {
                  name: "renewPassport",
                  value: isChecked ? "Yes" : "No", // 👈 send string
                },
              });

              if (isChecked)
                handleChange({
                  target: {
                    name: "noPassport",
                    value: "No",
                  },
                });

              if (isChecked && successSoundRef.current) {
                successSoundRef.current.currentTime = 0;
                successSoundRef.current.play();

                setTimeout(() => {
                  successSoundRef.current.pause();
                  successSoundRef.current.currentTime = 0;
                }, 2000);
              }
            }}
            label={
              <span className="whitespace-normal">
                My passport has expired or it's expiring within 6 months from
                the last day of the tour. I plan to renew it and I will revise
                the passport information on this form once I receive the
                renewal.
              </span>
            }
          />
        </div>
      </div> */}

      <div
        name="passportConfirmation"
        ref={inputRefs.passportConfirmation}
        className="md:col-span-2"
      >
        <div className="md:grid-cols-1">
          <Checkbox
            className="text-xs"
            name={`passportConfirmation${index}`}
            checked={formData.passportConfirmation}
            onChange={(e) => {
              const isChecked = e.target.checked;

              handleChange({
                target: {
                  name: "passportConfirmation",
                  value: isChecked,
                },
              });

              if (isChecked && successSoundRef.current) {
                successSoundRef.current.currentTime = 0;
                successSoundRef.current.play();

                setTimeout(() => {
                  successSoundRef.current.pause();
                  successSoundRef.current.currentTime = 0;
                }, 2000);
              }
            }}
            label="I confirm that the passport information I entered is correct"
            required
          />

          {formErrors.passportConfirmation && (
            <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
              <img src={Info} alt="info" />
              {formErrors.passportConfirmation}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelDetails;
